/**
 * CreateGroupDialog Component
 * Dialog for creating new local groups
 *
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import {
  Add as AddIcon,
} from '@mui/icons-material';
import {
  Button,
  TextField,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDialog } from '../ui/BaseDialog';
import { useCreateGroup } from '../../hooks/api/useGroups';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateGroupForm {
  name: string;
  description: string;
}



export const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const createGroupMutation = useCreateGroup();
  
  const [form, setForm] = useState<CreateGroupForm>({
    name: '',
    description: '',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        description: '',
      });
      createGroupMutation.reset();
    }
  }, [open]);

  const handleChange = (field: keyof CreateGroupForm, value: any) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    // Validate form
    if (!form.name.trim()) {
      return;
    }

    if (form.name.length < 3 || form.name.length > 100) {
      return;
    }

    // Create group using TanStack Query mutation
    const groupData = {
      displayName: form.name.trim(),
      description: form.description.trim(),
      type: 'team' as const,
      tags: [],
      source: 'local' as const,
    };
    
    createGroupMutation.mutate(groupData, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
    });
  };

  const isValid = form.name.trim().length >= 3;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t('groups.create.title')}
      maxWidth="sm"
      fullWidth
      loading={createGroupMutation.isPending}
      error={
        createGroupMutation.error instanceof Error
          ? createGroupMutation.error.message
          : createGroupMutation.error
          ? t('groups.create.error')
          : null
      }
      actions={
        <>
          <Button onClick={onClose}>
            {t('groups.create.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!isValid}
          >
            {t('groups.create.create')}
          </Button>
        </>
      }
    >
      <Box sx={{ pt: 1 }}>
        {/* Group Name */}
        <TextField
          autoFocus
          fullWidth
          label={t('groups.create.name')}
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder={t('groups.create.namePlaceholder')}
          helperText={t('groups.create.nameHelper')}
          sx={{ mb: 3 }}
          error={form.name.length > 0 && form.name.length < 3}
        />

        {/* Description */}
        <TextField
          fullWidth
          label={t('groups.create.description')}
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder={t('groups.create.descriptionPlaceholder')}
          multiline
          rows={3}
          helperText={t('groups.create.descriptionHelper')}
          sx={{ mb: 3 }}
        />

        {/* Info Box */}
        <Alert severity="info">
          <Typography variant="body2">
            {t('groups.create.localGroupsInfo')}
          </Typography>
        </Alert>
      </Box>
    </BaseDialog>
  );
};